import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveLine } from '@nivo/line';
import { Fetch } from '../../utils/Utils.js';
import { formatDate } from '../../../utils/dateFormat.js';
import '../reproduction/NivoChartCard.css';

// Fixed categorical hue order — CVD-safety mechanism is the order, never
// reassigned/cycled per render. "All" (the ranch-wide average) is its own
// neutral ink color, not a categorical slot — it's an aggregate overlay on
// top of the per-area identities, not one more identity competing with them.
const SERIES_COLORS = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'];
const ALL_COLOR = '#1a1714';

// Format "2026-01" to "Jan 2026" (or "fev. de 2026" in PT)
const formatMonthLabel = (yearMonth) => {
  const [year, month] = yearMonth.split('-');
  const date = new Date(year, parseInt(month) - 1);
  return formatDate(date, { month: 'short', year: 'numeric' });
};

const TemperatureChart = () => {
  const { t } = useTranslation();
  const [series, setSeries] = useState(null);
  const [legendItems, setLegendItems] = useState([]);
  const [summary, setSummary] = useState('');

  useEffect(() => {
    Fetch('/api/data/temperatures', 'GET', null, (rawData) => build(rawData || []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const build = (rawData) => {
    const agg = {};
    const months = [];
    const areas = [];

    rawData.forEach(row => {
      const month = (row.date || '').slice(0, 7);
      const area = row.area || t('unknown');
      if (!month) return;

      if (!agg[month]) agg[month] = {};
      if (!agg[month].all) agg[month].all = { total: 0, count: 0 };
      if (!agg[month][area]) agg[month][area] = { total: 0, count: 0 };

      agg[month].all.total += row.temperature;
      agg[month].all.count++;
      agg[month][area].total += row.temperature;
      agg[month][area].count++;

      if (!months.includes(month)) months.push(month);
      if (!areas.includes(area)) areas.push(area);
    });

    if (months.length === 0) {
      setSummary('');
      setSeries(null);
      return;
    }

    months.sort();
    areas.sort();

    // Gaps (null) for months an area had no readings — an area with no data
    // that month is "no data," not "0°C," same reasoning as the reproduction
    // trend chart's line gaps.
    const avgFor = (month, key) => {
      const cell = agg[month]?.[key];
      return cell ? Math.round((cell.total / cell.count) * 10) / 10 : null;
    };

    const built = [
      {
        id: t('all'),
        color: ALL_COLOR,
        emphasis: true,
        data: months.map(month => ({ x: month, y: avgFor(month, 'all') })),
      },
      ...areas.map((area, i) => ({
        id: area,
        color: SERIES_COLORS[i % SERIES_COLORS.length],
        data: months.map(month => ({ x: month, y: avgFor(month, area) })),
      })),
    ];

    const recentMonth = months[months.length - 1];
    const recentAvg = avgFor(recentMonth, 'all');
    setSummary(recentAvg !== null
      ? t('chart_summary_temperature', { month: formatMonthLabel(recentMonth), temp: Math.round(recentAvg) })
      : '');
    setSeries(built);
    setLegendItems(built.map(s => ({ id: s.id, color: s.color, emphasis: s.emphasis })));
  };

  return (
    <div className="Chart NivoCard TemperatureChart">
      <h3>{t('temperature_by_month')}</h3>
      {summary && <p className="nivo-card-subtitle">{summary}</p>}

      {!series ? (
        <p className="nivo-card-empty">{t('chart_summary_no_temperature')}</p>
      ) : (
        <>
          <ul className="nivo-legend-row">
            {legendItems.map(item => (
              <li key={item.id} style={item.emphasis ? { fontWeight: 700 } : undefined}>
                <span className="nivo-legend-dot" style={{ background: item.color }} />
                {item.id}
              </li>
            ))}
          </ul>
          <div style={{ height: 340 }}>
            <ResponsiveLine
              data={series}
              margin={{ top: 16, right: 30, bottom: 50, left: 56 }}
              xScale={{ type: 'point' }}
              yScale={{ type: 'linear' }}
              axisLeft={{ legend: t('temperature_celsius'), legendOffset: -46, legendPosition: 'middle', format: (v) => `${v}°` }}
              axisBottom={{ format: formatMonthLabel, tickRotation: series[0].data.length > 6 ? -30 : 0 }}
              colors={{ datum: 'color' }}
              lineWidth={2}
              pointSize={7}
              useMesh={true}
              enableGridX={false}
              motionConfig="gentle"
              theme={{
                axis: { ticks: { text: { fill: '#898781', fontSize: 12 } }, legend: { text: { fill: '#52514e', fontSize: 12.5 } } },
                grid: { line: { stroke: '#e1e0d9', strokeWidth: 1 } },
              }}
              tooltip={({ point }) => (
                <div className="nivo-tooltip">
                  <span className="nivo-tooltip-swatch" style={{ background: point.seriesColor }} />
                  <strong>{point.seriesId}</strong>: <span className="nivo-tooltip-value">{point.data.y}°C</span> ({formatMonthLabel(point.data.x)})
                </div>
              )}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default TemperatureChart;
